import React, { ReactNode, useCallback, useEffect } from 'react';
import moment from 'moment';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  FinanceProgramType,
  MarkupType,
  PaymentPeriodTiming,
  PortalConfigurationType,
  SubsidyCapType,
} from '@trnsact/trnsact-shared-types';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { FinanceProgramForm, FinanceProgramTierForm } from '../../../../schema';
import { DateFormats } from '../../../../../../constants/common';
import { MarkupMethods, Structures } from '../../../../types';
import {
  markupOptions,
  paymentCalculationMethodOptions,
  programTypeOptions,
  structureOptions,
  booleanOptions,
  capTypeOptions,
  feesMarkupTypesOptions,
} from '../../../../constants';
import {
  CurrencyInputField,
  DateField,
  FormRadioGroup,
  InputField,
  InterestInputField,
  RadioGroupField,
  SelectField,
  SwitchField,
} from '../../../../../../components/form';
import { ScoreTierEntity } from 'pages/LenderWaterfall/ScorecardBuilder/types';
import { FinanceProgramTiers } from './FinanceProgramTiers';
import { RateType } from './RateType';
import { SubsidySection } from './SubsidySection';
import { FormSection } from '../../FormSection';
import { getFinanceProgramInitialFormValues } from '../../../../lib';

interface Props {
  isReadOnly: boolean;
  scoreTiers?: ScoreTierEntity[];
}

export const ConfigureStructure = ({ isReadOnly, scoreTiers }: Props) => {
  const { control, setValue, resetField } = useFormContext<FinanceProgramForm>();
  const secondStep = useWatch({ control, name: 'program.second' });

  const isSubsidyCalculatorEnabled = !!useSelector((state: any) =>
    portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.Portal)
  )?.jsonDefinition?.config?.subsidyCalculator;

  const {
    fields: feeFields,
    append,
    remove,
    update,
  } = useFieldArray<FinanceProgramForm>({ control, name: 'program.second.fee' });

  const feeWatchFields = useWatch({
    control,
    name: 'program.second.fee',
  });

  const onFinanceProgramTiersChange = (tiers: FinanceProgramTierForm[]) => {
    setValue('program.second.tiers', tiers);
  };

  const isFinanceProgramTiersAvailable = !!scoreTiers?.length;

  const initialSecond = getFinanceProgramInitialFormValues(scoreTiers).program.second;

  const handleResetApprovalTerms = useCallback(() => {
    setValue('program.second', {
      ...secondStep,
      rateType: initialSecond.rateType,
      terms: initialSecond.terms,
      rate: initialSecond.rate,
      specifyRates: initialSecond.specifyRates,
      isSpecifyRate: initialSecond.isSpecifyRate,
      isTiersEnabled: initialSecond.isTiersEnabled,
      tiers: initialSecond.tiers,
    });
  }, [setValue, initialSecond, secondStep]);

  const handleResetProgramStructure = useCallback(() => {
    setValue('program.second', {
      ...secondStep,
      structure: initialSecond.structure,
      method: initialSecond.method,
      customerRate: initialSecond.customerRate,
      dealerMarkup: initialSecond.dealerMarkup,
      programType: initialSecond.programType,
      daysToFirstPayment: initialSecond.daysToFirstPayment,
      advancePayment: initialSecond.advancePayment,
      minimumRequiredDownPayment: initialSecond.minimumRequiredDownPayment,
      paymentCalculationMethod: initialSecond.paymentCalculationMethod,
      residual: initialSecond.residual,
      isValidityDates: initialSecond.isValidityDates,
      startDate: initialSecond.startDate,
      endDate: initialSecond.endDate,
      configureSubsidyCap: initialSecond.configureSubsidyCap,
      subsidyCapConfiguration: initialSecond.subsidyCapConfiguration,
    });
  }, [setValue, initialSecond, secondStep]);

  const handleResetSubsidy = useCallback(() => {
    resetField('program.second.subsidyPercent');
    resetField('program.second.dnetPercent');
    setValue('program.second.subsidyTerms', []);
    setValue('program.second.isSpecifySubsidy', false);
    setValue('program.second.specifySubsidyRates', []);
  }, [resetField, setValue]);

  const handleResetFees = useCallback(() => {
    setValue('program.second.fee', []);
  }, [setValue]);

  // If I don't have this, the form does not initialize the subsidyCapConfiguration field
  useEffect(() => {
    if (secondStep.subsidyCapConfiguration) {
      setValue(
        'program.second.specifyLenderMarketRates',
        secondStep.specifyRates.map(r => {
          const rate =
            secondStep.subsidyCapConfiguration?.lenderMarketRates?.find(l => l.term === Number(r.term))?.rate ?? 0;

          return { term: Number(r.term), rate: rate };
        })
      );
      setValue('program.second.subsidyCapConfiguration', secondStep.subsidyCapConfiguration);
    } else {
      setValue('program.second.subsidyCapConfiguration', {
        subsidyCapType: SubsidyCapType.FixedAmount,
        lenderMarketRates: [],
        lenderName: '',
        maxSubsidyAmount: 0,
        netInvoicePercentage: 0,
      });
    }
  }, [secondStep.isSpecifyRate, secondStep.configureSubsidyCap]);

  const structurePartByType: Record<Structures, ReactNode> = {
    [Structures.Markup]: (
      <>
        <RadioGroupField label="Method" name="program.second.method" control={control} options={markupOptions} row />

        {secondStep.method === MarkupMethods.CustomerRate && (
          <InterestInputField
            control={control}
            name="program.second.customerRate"
            label="Customer Rate (%)"
            textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
          />
        )}

        {secondStep.method === MarkupMethods.Markup && (
          <InterestInputField
            control={control}
            name="program.second.dealerMarkup"
            label="Dealer Markup (%)"
            textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
          />
        )}
      </>
    ),
    [Structures.Subsidy]: (
      <>
        <Box className="subsidyCapConfiguration">
          <Box className="formRow">
            <SelectField
              control={control}
              options={booleanOptions}
              label="Configure Subsidy Cap"
              name="program.second.configureSubsidyCap"
            />

            <RenderWithCondition condition={secondStep.configureSubsidyCap}>
              <>
                <InputField
                  control={control}
                  label="Lender Name"
                  inputProps={{ required: true }}
                  name="program.second.subsidyCapConfiguration.lenderName"
                />

                <InterestInputField
                  required
                  control={control}
                  label="Lender Market Rate"
                  name="program.second.commonLenderMarketRate"
                  textFieldProps={{ fullWidth: true, disabled: secondStep.isSpecifyRate }}
                />
              </>
            </RenderWithCondition>
          </Box>

          <RenderWithCondition condition={secondStep.configureSubsidyCap}>
            <Box className="formRow">
              <SelectField
                control={control}
                label="Cap Type"
                options={capTypeOptions}
                name="program.second.subsidyCapConfiguration.subsidyCapType"
              />

              {secondStep.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.FixedAmount ? (
                <CurrencyInputField
                  control={control}
                  label="Subsidy Cap Amount"
                  textFieldProps={{ required: true }}
                  name="program.second.subsidyCapConfiguration.maxSubsidyAmount"
                />
              ) : (
                secondStep.subsidyCapConfiguration?.subsidyCapType === SubsidyCapType.NetInvoicePercentage && (
                  <InterestInputField
                    control={control}
                    label="Subsidy Cap Percentage"
                    textFieldProps={{ required: true }}
                    name="program.second.subsidyCapConfiguration.netInvoicePercentage"
                  />
                )
              )}
            </Box>
          </RenderWithCondition>
        </Box>
      </>
    ),
    [Structures.None]: null,
  };

  const isDaysToFirstPaymentGTZero = Boolean(secondStep.daysToFirstPayment ? +secondStep.daysToFirstPayment : false);

  return (
    <>
      <FormSection title="Approval Terms" onReset={handleResetApprovalTerms} isReadOnly={isReadOnly}>
        {isFinanceProgramTiersAvailable && (
          <Box className="formRow">
            <SwitchField
              control={control}
              disabled={isReadOnly}
              label="Enable Tiers"
              name="program.second.isTiersEnabled"
            />
          </Box>
        )}

        {secondStep.isTiersEnabled ? (
          <FinanceProgramTiers
            isReadOnly={isReadOnly}
            scoreTiers={scoreTiers!}
            financeProgramTiers={secondStep.tiers}
            onListingChange={onFinanceProgramTiersChange}
          />
        ) : (
          <RateType isReadOnly={isReadOnly} />
        )}
      </FormSection>

      <FormSection title="Program Structure" onReset={handleResetProgramStructure} isReadOnly={isReadOnly}>
        <RadioGroupField
          row
          label="Structure"
          control={control}
          options={structureOptions}
          name="program.second.structure"
          formControlProps={{ disabled: isReadOnly }}
        />

        <RenderWithCondition condition={!!secondStep.structure}>
          {structurePartByType[secondStep.structure]}
        </RenderWithCondition>

        <Box className="formRow">
          <SelectField
            required
            control={control}
            label="Program Type"
            disabled={isReadOnly}
            options={programTypeOptions}
            name="program.second.programType"
          />

          {!secondStep.isTiersEnabled && (
            <InputField
              control={control}
              label="Advance Payment (# of)"
              name="program.second.advancePayment"
              inputProps={{ type: 'number', required: true, disabled: isReadOnly, placeholder: 'Enter value' }}
            />
          )}

          <InputField
            control={control}
            label="Days to First Payment"
            name="program.second.daysToFirstPayment"
            inputProps={{ type: 'number', required: false, disabled: isReadOnly }}
            extraAction={{
              cb: nextValue => {
                if (!!+nextValue) {
                  setValue('program.second.paymentCalculationMethod', PaymentPeriodTiming.BeginningOfPeriod);
                }
              },
            }}
          />
        </Box>

        <Box className="formRow">
          <Tooltip
            arrow
            title={!isDaysToFirstPaymentGTZero ? '' : 'To Calculate in Arrears, set Days to First Payment to 0'}
          >
            <span style={{ width: '100%' }}>
              <SelectField
                required
                control={control}
                label="Payment Calculation Method"
                options={paymentCalculationMethodOptions}
                name="program.second.paymentCalculationMethod"
                disabled={isReadOnly || isDaysToFirstPaymentGTZero}
              />
            </span>
          </Tooltip>

          {!secondStep.isTiersEnabled && (
            <InterestInputField
              control={control}
              label="Minimum required down payment (%)"
              name="program.second.minimumRequiredDownPayment"
              textFieldProps={{ fullWidth: true, disabled: isReadOnly }}
            />
          )}

          {secondStep.programType === FinanceProgramType.Lease && (
            <InterestInputField
              control={control}
              label="Residual %"
              name="program.second.residual"
              textFieldProps={{ fullWidth: true, required: true, disabled: isReadOnly }}
            />
          )}
        </Box>

        <SwitchField
          control={control}
          disabled={isReadOnly}
          label="Validity Dates"
          name="program.second.isValidityDates"
        />

        {secondStep.isValidityDates && (
          <Box className="formRow">
            <DateField
              control={control}
              label="Start Date"
              disabled={isReadOnly}
              format={DateFormats.Short}
              name="program.second.startDate"
              slotProps={{ textField: { required: true } }}
            />

            <DateField
              label="End Date"
              control={control}
              disabled={isReadOnly}
              format={DateFormats.Short}
              name="program.second.endDate"
              slotProps={{ textField: { required: true } }}
              minDate={secondStep.startDate ? moment(secondStep.startDate) : moment()}
            />
          </Box>
        )}
      </FormSection>

      {isSubsidyCalculatorEnabled && (
        <FormSection title="Subsidy" onReset={handleResetSubsidy} isReadOnly={isReadOnly}>
          <SubsidySection isReadOnly={isReadOnly} />
        </FormSection>
      )}

      <FormSection title="Fees" onReset={handleResetFees} isReadOnly={isReadOnly}>
        <Box>
          {!!feeFields?.length &&
            feeFields.map((feeField, index) => (
              <Box key={feeField.id}>
                <Box className="arrayField">
                  <Box className="arrayFieldHeader">
                    <IconButton
                      size="small"
                      disabled={isReadOnly}
                      className="customIconBtn"
                      onClick={() => remove(index)}
                    >
                      <DeleteOutline color="error" />
                    </IconButton>

                    <Typography component="span" variant="subtitle2">
                      Fee: {index + 1}
                    </Typography>
                  </Box>

                  <Box className="formRow">
                    <InputField
                      control={control}
                      label="Description"
                      name={`program.second.fee.${index}.description`}
                      inputProps={{
                        disabled: isReadOnly,
                      }}
                    />

                    <Box sx={{ width: '100%' }}>
                      {feeField.type === MarkupType.Flat ? (
                        <CurrencyInputField
                          control={control}
                          label="Fee ($)"
                          name={`program.second.fee.${index}.amount`}
                          textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
                        />
                      ) : (
                        <InterestInputField
                          label="Fee (%)"
                          control={control}
                          name={`program.second.fee.${index}.amount`}
                          textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
                        />
                      )}
                      <Controller
                        control={control}
                        name={`program.second.fee.${index}.type`}
                        render={({ field }) => (
                          <FormRadioGroup
                            row
                            options={feesMarkupTypesOptions}
                            {...field}
                            onChange={(event, value) =>
                              update(index, {
                                ...feeWatchFields[index],
                                type: value as MarkupType,
                              })
                            }
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                <SwitchField
                  label="Financed?"
                  control={control}
                  disabled={isReadOnly}
                  name={`program.second.fee.${index}.isFinanced`}
                />
              </Box>
            ))}
        </Box>

        <Box>
          <Button
            disabled={isReadOnly}
            startIcon={<AddCircleOutline />}
            onClick={() => append({ amount: 0, description: '', isFinanced: false, type: MarkupType.Flat })}
          >
            Add Fee
          </Button>
        </Box>
      </FormSection>
    </>
  );
};
